import { useCallback, useMemo } from 'react';

import moment, { Moment } from 'moment-timezone';

import { USADateTimeFormat, apiDateTimeFormat } from '../../utils';

export const useLocationTimezone = (timeZone: string) => {
  const zoneDateTime = useMemo(() => moment.utc().tz(timeZone), [timeZone]);

  const timeZoneShortName = useMemo(() => moment.tz(timeZone).format('z'), [timeZone]);

  const isBrowserTimezone = useMemo(() => {
    if (!timeZone) {
      return true;
    }

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = Date.now();

    const browserOffset = moment.tz.zone(browserTimezone)?.utcOffset(now);
    const locationOffset = moment.tz.zone(timeZone)?.utcOffset(now);

    return browserOffset !== undefined && browserOffset === locationOffset;
  }, [timeZone]);

  const getCurrentZoneDateTime = useCallback(() => moment.utc().tz(timeZone), [timeZone]);

  const utcToZone = useCallback(
    <R extends 'string' | 'date' | 'moment' = 'string'>(
      dateTime: string,
      returnType: R = 'string' as R,
      format?: string,
    ): ReturnTypeBasedOnValue<R> => {
      const date = moment.utc(dateTime);

      if (!date.isValid()) {
        throw new Error('Invalid date');
      }

      const zoneDate = date.tz(timeZone);

      if (returnType === 'moment') {
        return zoneDate as ReturnTypeBasedOnValue<R>;
      }

      if (returnType === 'date') {
        return new Date(
          zoneDate.year(),
          zoneDate.month(),
          zoneDate.date(),
          zoneDate.hour(),
          zoneDate.minute(),
          zoneDate.second(),
          zoneDate.millisecond(),
        ) as ReturnTypeBasedOnValue<R>;
      }

      return zoneDate.format(format || USADateTimeFormat) as ReturnTypeBasedOnValue<R>;
    },
    [timeZone],
  );

  const zoneToUTC = useCallback(
    <R extends 'string' | 'date' | 'moment' = 'string'>(
      dateTime: string,
      returnType: R = 'string' as R,
    ): ReturnTypeBasedOnValue<R> => {
      const date = moment(dateTime);

      if (!date.isValid()) {
        throw new Error('Invalid date');
      }

      const utcDate = date.utc();

      if (returnType === 'moment') {
        return utcDate as ReturnTypeBasedOnValue<R>;
      }

      if (returnType === 'date') {
        return new Date(
          utcDate.year(),
          utcDate.month(),
          utcDate.date(),
          utcDate.hour(),
          utcDate.minute(),
          utcDate.second(),
          utcDate.millisecond(),
        ) as ReturnTypeBasedOnValue<R>;
      }

      return utcDate.format(apiDateTimeFormat) as ReturnTypeBasedOnValue<R>;
    },
    [],
  );

  const getTzAbbr = useCallback((dateTime: string) => moment(dateTime).tz(timeZone).zoneAbbr(), [timeZone]);

  return {
    zoneDateTime,
    timeZoneShortName,
    isBrowserTimezone,
    getCurrentZoneDateTime,
    utcToZone,
    zoneToUTC,
    getTzAbbr,
  };
};

type ReturnTypeBasedOnValue<R> = R extends 'moment' ? Moment : R extends 'date' ? Date : string;
